import React from 'react'
import { displayDollar } from '../../../lib/utils/helpers'
import { Withdrawal, WithdrawalStatus } from '../../../lib/types'
import {
  canCancelOngoingWithdrawal,
  shouldShowWithdrawalProgressCard,
  withUpperScaleSuffix,
} from '../dashboard-v2.utils'
import { DashboardV2ProgressStepper, DashboardV2StepPhase } from './DashboardV2ProgressStepper'

type WithdrawalsSectionProps = {
  withdrawals: Withdrawal[]
  onWithdrawalViewDetails?: (withdrawal: Withdrawal) => void
  onWithdrawalCancel?: (withdrawal: Withdrawal) => void
}

const getStepPhases = (
  status: string,
): {
  request: DashboardV2StepPhase
  executed: DashboardV2StepPhase
} => {
  switch (status) {
    case WithdrawalStatus.TRADE_EXECUTED:
      return { request: 'done', executed: 'done' }
    case WithdrawalStatus.ORDER_PLACED:
      return { request: 'done', executed: 'current' }
    case WithdrawalStatus.REJECTED:
    case WithdrawalStatus.CANCELLED:
      return { request: 'done', executed: 'upcoming' }
    case WithdrawalStatus.INITIATED:
    default:
      return { request: 'current', executed: 'upcoming' }
  }
}

const redemptionFooterMessage = (status: string): string => {
  switch (status) {
    case WithdrawalStatus.INITIATED:
      return 'Redemption request received'
    case WithdrawalStatus.ORDER_PLACED:
      return 'Order placed — awaiting execution'
    case WithdrawalStatus.TRADE_EXECUTED:
      return 'Credited to your wallet — reinvest or withdraw anytime'
    case WithdrawalStatus.REJECTED:
      return 'Redemption rejected'
    case WithdrawalStatus.CANCELLED:
      return 'Redemption cancelled'
    default:
      return 'In progress'
  }
}

export const WithdrawalsSection: React.FC<WithdrawalsSectionProps> = ({
  withdrawals,
  onWithdrawalViewDetails,
  onWithdrawalCancel,
}) => {
  const visibleWithdrawals = withdrawals.filter((wd) => shouldShowWithdrawalProgressCard(wd.status))

  return (
    <section className='dashboard-v2__withdrawals' data-testid='dashboard-withdrawals'>
      <div className='card-head'>
        <h2>Redemptions</h2>
      </div>
      {visibleWithdrawals.length === 0 ? (
        <p className='dashboard-v2__withdrawals-empty'>No redemption requests yet.</p>
      ) : (
        <div className='dashboard-v2__withdrawal-grid'>
          {visibleWithdrawals.map((wd) => {
            const canViewDetails = typeof onWithdrawalViewDetails === 'function'
            const canCancel =
              typeof onWithdrawalCancel === 'function' && canCancelOngoingWithdrawal(wd.status)
            const phases = getStepPhases(wd.status)
            const amountDisplay = withUpperScaleSuffix(
              displayDollar(String(wd.amount), 'USD', 2, false),
            )

            return (
              <article key={wd.id} className='dashboard-v2__withdrawal-card'>
                <div className='dashboard-v2__withdrawal-card__head'>
                  <div className='dashboard-v2__withdrawal-card__titles'>
                    <h3 className='dashboard-v2__withdrawal-card__title'>
                      {wd.opportunity_title || 'Redemption'}
                    </h3>
                    <p className='dashboard-v2__withdrawal-card__subscription'>
                      Redemption Amount: <strong>{amountDisplay}</strong>
                    </p>
                  </div>
                </div>

                <DashboardV2ProgressStepper request={phases.request} executed={phases.executed} />

                <div className='dashboard-v2__withdrawal-card__footer'>
                  <span className='dashboard-v2__withdrawal-card__footer-status'>
                    {redemptionFooterMessage(wd.status)}
                  </span>
                  {canCancel || canViewDetails ? (
                    <div className='dashboard-v2__withdrawal-card__footer-actions'>
                      {canViewDetails ? (
                        <button
                          type='button'
                          className='dashboard-v2__withdrawal-card__footer-link'
                          onClick={() => onWithdrawalViewDetails(wd)}
                          data-testid={`view-ongoing-withdrawal-${wd.id}`}
                        >
                          View details
                        </button>
                      ) : null}
                      {canCancel ? (
                        <button
                          type='button'
                          className='dashboard-v2__withdrawal-card__footer-cancel'
                          onClick={() => onWithdrawalCancel(wd)}
                          data-testid={`cancel-ongoing-withdrawal-${wd.id}`}
                        >
                          Cancel
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </article>
            )
          })}
        </div>
      )}
    </section>
  )
}
